import React, {useMemo, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import {decode} from 'html-entities';
import {
  Box,
  Typography,
  Stack,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel,
  styled,
} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import {ArrowLeft} from 'lucide-react';

import {useGetDocumentsSharedWithMeQuery} from '../../data/documentWorkspaceApi';
import {sortRecords} from '../../util';
import Loading from '../../components/Loading';
import TimeAgo from '../../components/TimeAgo';
import DocumentIcon from '../../assets/file-text.svg';
import {DARK_TEXT_COLOR, GREY_TEXT_SECONDARY} from '../../theme';
import {sharedStyles} from './ExternalUserLandingPage';
import DocumentStatusChip from '../../components/documents/DocumentStatusChip';

const TABLE_HEADERS = [
  {
    label: 'Name',
    id: 'name',
    sortable: true,
    width: 'auto',
    maxWidth: '30.25rem',
  },
  {label: 'File type', id: 'type', sortable: true, width: '12.5rem'},
  {label: 'Status', id: 'workflowStatus', sortable: true, width: '12.5rem'},
  {label: 'Last modified', id: 'updatedAt', sortable: true, width: '16.75rem'},
];

const StyledTableRow = styled(TableRow)(() => ({
  '& td': {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #DFE5E2',
    borderBottom: '1px solid #DFE5E2',
  },
  '& td:first-of-type': {
    borderLeft: '1px solid #DFE5E2',
    borderRadius: '8px 0 0 8px',
  },
  '& td:last-of-type': {
    borderRight: '1px solid #DFE5E2',
    borderRadius: '0 8px 8px 0',
  },
  '&:hover td': {
    backgroundColor: '#EBFAF1 !important',
  },
}));

const styles = {
  backButton: {
    color: DARK_TEXT_COLOR,
    fontWeight: 500,
    fontSize: '14px',
    textTransform: 'none',
    padding: '0.5rem 0',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  orgName: {
    color: DARK_TEXT_COLOR,
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '130%',
    letterSpacing: '-0.24px',
    marginTop: '0.5rem',
  },
  table: {
    container: {
      borderRadius: '0.75rem 0.75rem 0 0',
      border: '1px solid #DFE5E2',
      borderBottom: 'none',
      background: '#FFFFFF',
    },
    header: {
      cell: {
        height: '2.0625rem',
        backgroundColor: '#E4EAE7',
        padding: '0.5rem 1rem',
        alignItems: 'center',
      },
      label: {
        color: 'rgba(4, 59, 52, 0.70)',
        fontSize: '0.75rem',
        fontWeight: '600',
        lineHeight: '140%',
      },
    },
    row: {
      label: {
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '140%',
        color: '#043B34',
      },
      secondaryLabel: {
        fontSize: '0.75rem',
        fontWeight: '400',
        lineHeight: '140%',
        color: '#043B34',
        opacity: 0.6,
      },
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    textAlign: 'center',
  },
  emptyStateTitle: {
    color: DARK_TEXT_COLOR,
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '0.5rem',
    lineHeight: '150%',
  },
  emptyStateSubtitle: {
    color: GREY_TEXT_SECONDARY,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '140%',
  },
};

// Renders file type label matching DocumentsPage format
const renderFileType = (doc) => {
  switch (doc.type) {
    case 'ANNUAL':
      if (_.has(doc, 'fiscalYear')) {
        return `Annual report (${doc.fiscalYear})`;
      }
      return 'Annual report';
    case 'QUARTER':
      if (_.has(doc, 'fiscalYear') && _.has(doc, 'quarter')) {
        return `Quarterly report (${doc.quarter} ${doc.fiscalYear})`;
      } else if (_.has(doc, 'fiscalYear')) {
        return `Quarterly report (${doc.fiscalYear})`;
      }
      return 'Quarterly report';
    case 'SEMI_ANNUAL':
      if (_.has(doc, 'fiscalYear')) {
        return `Semi-Annual report (${doc.fiscalYear})`;
      }
      return 'Semi-Annual report';
    default:
      return 'Basic document';
  }
};

const ExternalUserDocumentsForOrg = () => {
  const {orgId} = useParams();
  const navigate = useNavigate();
  const {isExternalUser, accessToken} = useSelector((state) => state.session);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updatedAt');

  const {
    data: sharedWithMe,
    isFetching,
    isLoading,
  } = useGetDocumentsSharedWithMeQuery(undefined, {
    skip: !isExternalUser || !accessToken,
  });

  // Properly extract and memoize orgData based on the provided structure
  const sharedData = useMemo(() => {
    if (!sharedWithMe?.recordsByOrg || !orgId) return null;
    return sharedWithMe.recordsByOrg[orgId];
  }, [sharedWithMe, orgId]);

  // organization is a flat object, while documents is a separate array in orgData
  const organization = sharedData?.organization;
  const documents = sharedData?.documents || [];

  // Sort documents based on current order and orderBy
  const sortedDocuments = useMemo(() => {
    return sortRecords(documents, order, orderBy);
  }, [documents, order, orderBy]);

  const handleRequestSort = (property) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDocClick = (documentId) => {
    navigate(`/documents/${documentId}?shared=true`);
  };

  const handleBack = () => {
    navigate('/shared');
  };

  if (isFetching || isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{height: '50vh', width: '100%'}}
      >
        <Loading loading={true} message="Loading documents" />
      </Box>
    );
  }

  return (
    <>
      <div style={sharedStyles.stickyHeader}>
        <Button
          onClick={handleBack}
          sx={styles.backButton}
          startIcon={<ArrowLeft size={18} />}
          disableRipple
        >
          Back to organizations
        </Button>
        <Typography sx={styles.orgName}>
          {organization?.orgName || 'Organization'}
        </Typography>
      </div>
      <div style={sharedStyles.scrollableContent}>
        {documents.length === 0 ? (
          <Box sx={styles.emptyState}>
            <Typography variant="h6" sx={styles.emptyStateTitle}>
              No documents found.
            </Typography>
            <Typography variant="body2" sx={styles.emptyStateSubtitle}>
              Only documents shared with you from this organization will appear
              here.
            </Typography>
          </Box>
        ) : (
          <Stack direction="column" spacing="0.75rem">
            <TableContainer sx={styles.table.container}>
              <Table sx={{borderCollapse: 'separate'}}>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        borderTop: '1px solid #DFE5E2',
                        borderBottom: '1px solid #DFE5E2',
                        backgroundColor: '#E4EAE7',
                      },
                      '& th:first-of-type': {
                        borderLeft: '1px solid #DFE5E2',
                        borderRadius: '8px 0 0 8px',
                      },
                      '& th:last-of-type': {
                        borderRight: '1px solid #DFE5E2',
                        borderRadius: '0 8px 8px 0',
                      },
                    }}
                  >
                    {TABLE_HEADERS.map((header) => (
                      <TableCell
                        key={header.id}
                        variant="head"
                        sortDirection={orderBy === header.id ? order : false}
                        sx={{
                          ...styles.table.header.cell,
                          width: header.width,
                          maxWidth: header.maxWidth,
                        }}
                      >
                        {header.sortable ? (
                          <TableSortLabel
                            active={orderBy === header.id}
                            direction={orderBy === header.id ? order : 'asc'}
                            onClick={handleRequestSort(header.id)}
                          >
                            <Typography sx={styles.table.header.label}>
                              {header.label}
                            </Typography>
                            {orderBy === header.id ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc'
                                  ? 'sorted descending'
                                  : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        ) : (
                          <Typography sx={styles.table.header.label}>
                            {header.label}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
            <TableContainer>
              <Table
                sx={{borderCollapse: 'separate', borderSpacing: '0 0.375rem'}}
              >
                <TableBody>
                  {sortedDocuments.map((doc) => (
                    <StyledTableRow
                      key={doc.documentId}
                      sx={{cursor: 'pointer'}}
                      onClick={() => handleDocClick(doc.documentId)}
                    >
                      <TableCell
                        sx={{
                          width: TABLE_HEADERS[0].width,
                          maxWidth: TABLE_HEADERS[0].maxWidth,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src={DocumentIcon} alt="" />
                          <Typography noWrap sx={styles.table.row.label}>
                            {parse(decode(doc.name || 'Untitled document'))}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{width: TABLE_HEADERS[1].width}}>
                        <Typography sx={styles.table.row.label}>
                          {renderFileType(doc)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{width: TABLE_HEADERS[2].width}}>
                        <DocumentStatusChip
                          rowData={doc}
                          workflowStatus={doc.workflowStatus}
                          orgConfig={organization}
                        />
                      </TableCell>
                      <TableCell sx={{width: TABLE_HEADERS[3].width}}>
                        <Stack direction="column">
                          <Typography sx={styles.table.row.label}>
                            <TimeAgo timestamp={doc.updatedAt} />
                          </Typography>
                          <Typography sx={styles.table.row.secondaryLabel}>
                            By {doc.updatedByName || 'Unknown'}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </div>
    </>
  );
};

export default ExternalUserDocumentsForOrg;
